<template>
  <div
    class="w-full bg-brand-yellow-yellow-light rounded-l"
    :class="{
      'px-200 pt-200 pb-100': !dense,
      'p-150': dense,
    }"
  >
    <div
      class="flex flex-col items-center bg-accent-win-win rounded-l"
      :class="{
        'py-750': !dense,
        'pt-750 pb-400': dense,
      }"
    >
      <div class="animation-container flex justify-center">
        <div
          class="win inline-block px-150 py-150 bg-primary-primary-disabled rounded-xl"
          :class="{
            'show-win': currentAnimationStep?.showWin,
            'mb-950': !dense,
            'mb-500': dense,
          }"
        >
          <div class="px-150 py-150 bg-primary-primary-disabled rounded-l">
            <div class="px-200 py-100 bg-accent-win-win-bold rounded-l price-shadow text-center">
              <WinningAnimationMessage
                v-bind="{
                  freeTickets,
                  isPendingWinnings,
                  isTotalWinnings,
                  type,
                }"
                :win="prize ?? undefined"
              />
            </div>
          </div>
        </div>
        <Illustration v-if="illustration" class="illustration" :name="illustration" />
      </div>

      <p
        class="win-text text-center"
        :class="{
          'headline-h3-headline': !dense,
          'headline-h5-headline': dense,
        }"
      >
        {{ winningTextHeadline }}
      </p>
      <slot name="subline" />
    </div>
  </div>
</template>

<script lang="ts" setup>
const { t: $t } = useI18n()

import confetti from 'canvas-confetti'

import type { Money } from '#api/shared/@types/generated/backend/graphql-schema-types'

type AnimationStep = {
  delay?: number
  duration: number
  illustration: string
  once?: boolean
  showWin?: boolean
}

type WinningAnimationCardProps = {
  dense?: boolean
  freeTickets?: number
  isBigWin?: boolean
  isPendingWinnings?: boolean
  isTotalWinnings?: boolean
  prize: Maybe<Money>
  type?: 'normal' | 'small'
}

const { isBigWin, isPendingWinnings, prize, type } = defineProps<WinningAnimationCardProps>()

const prefersReducedMotion = useReducedMotion()

const winningTextHeadline = computed(() => {
  if (isPendingWinnings) return $t('winninganimation.text.pending.headline')
  if (isBigWin) return $t('winninganimation.text.bigwin')
  return $t('winninganimation.text')
})

const animationSteps = ref<AnimationStep[]>([
  {
    delay: 200,
    duration: 3000,
    illustration: isBigWin ? 'scratchcard-big-win' : 'scratchcard-win',
    once: true,
  },
  {
    delay: 750,
    duration: 1500,
    illustration: 'scratchcard-win-stars',
    once: true,
    showWin: true,
  },
  {
    duration: 1500,
    illustration: 'scratchcard-win-stars-loop',
    showWin: true,
  },
])

const illustration = ref<string>()
const currentAnimationStep = ref<AnimationStep>()

const animateWin = () => {
  const nextStep = animationSteps.value.shift()
  if (!nextStep) return

  currentAnimationStep.value = nextStep

  if (currentAnimationStep.value) {
    const step = currentAnimationStep.value
    setTimeout(() => {
      illustration.value = step.illustration

      setTimeout(() => {
        animateWin()
        if (step.once) {
          illustration.value = undefined
        }
      }, step.duration)
    }, step.delay)
  }
}

const shapes = [
  confetti.shapeFromPath(
    'M0.258301 1.66895L5.04249 0.56911L8.89485 11.157L3.05884 12.6459L0.258301 1.66895Z',
  ),
  confetti.shapeFromPath(
    'M6.20198 0.320671C6.4076 0.127036 6.746 0.253229 6.77462 0.534216L7.04229 3.16184C7.05366 3.27342 7.1192 3.37227 7.21756 3.42616L9.53388 4.6953C9.78158 4.83102 9.76613 5.19185 9.50774 5.3059L7.09143 6.37245C6.98883 6.41774 6.91508 6.51062 6.89422 6.62082L6.40297 9.21596C6.35044 9.49348 6.0025 9.59029 5.81418 9.37979L4.05315 7.41133C3.97837 7.32774 3.86725 7.2863 3.75599 7.30051L1.13607 7.63525C0.855902 7.67105 0.656307 7.37005 0.79831 7.1259L2.12624 4.84278C2.18263 4.74582 2.18771 4.62733 2.13981 4.52592L1.01185 2.13766C0.891229 1.88227 1.11582 1.59943 1.3919 1.65904L3.97363 2.21645C4.08326 2.24012 4.19752 2.20833 4.27917 2.13144L6.20198 0.320671Z',
  ),
  confetti.shapeFromPath(
    'M8.23054 3.44791C8.42553 5.50784 6.9137 7.33583 4.85376 7.53083C2.79382 7.72583 0.965834 6.21399 0.770837 4.15405C0.57584 2.09411 2.08767 0.266127 4.14761 0.0711298C6.20755 -0.123867 8.03554 1.38797 8.23054 3.44791Z',
  ),
]

let confettiTimeout: NodeJS.Timeout | undefined

onMounted(() => {
  if (prefersReducedMotion.value) {
    currentAnimationStep.value = { duration: 0, illustration: '', showWin: true }
    return
  }

  animateWin()

  confettiTimeout = setTimeout(() => {
    confetti({
      colors: ['#ffba45', '#fff345', '#ff9345'],
      gravity: 0.7,
      origin: { y: 0.4 },
      particleCount: 500,
      scalar: 1.2,
      shapes,
      spread: 500,
      ticks: 500,
      zIndex: 99999,
    })
  }, 1150)
})

onUnmounted(() => {
  clearTimeout(confettiTimeout)
})
</script>

<style lang="scss">
.price-shadow {
  box-shadow: token('shadow/shadow/card/small');
}

.win-text {
  z-index: 1;
  white-space: pre-line;
}

.animation-container {
  position: relative;
  width: 100%;

  .illustration {
    position: absolute;
    top: -4.5rem;
    left: 50%;
    min-width: 18rem;
    transform: translateX(-50%);
  }

  .win {
    opacity: 0;
    transition: all 0.7s cubic-bezier(1, 0.01, 0.6, 0.75);
    transform: scale(0);
  }

  .show-win {
    width: auto;
    opacity: 1;
    transform: scale(1);
  }

  .prize {
    opacity: 0;
  }

  .show-win .prize {
    opacity: 1;
    transition: all 1s ease-in 0.9s;
  }
}
</style>
